import React, { useCallback, useEffect, useState } from "react";
import { Building2, Check, Copy, KeyRound, X } from "lucide-react";
import { toast } from "react-toastify";
import { membersApi, membersErr, SsoConnectReq, SsoProvider, SsoSetupInfo } from "../../api/members/membersApi";

const PROVIDERS: { key: SsoConnectReq["provider"]; label: string; hint: string }[] = [
  { key: "google", label: "Google Workspace", hint: "Sign in with company Google accounts" },
  { key: "microsoft", label: "Microsoft Entra ID", hint: "Azure AD / Microsoft 365 accounts" },
  { key: "oidc", label: "Other (OpenID Connect)", hint: "Okta, Keycloak, Auth0, JumpCloud..." },
  { key: "saml", label: "Other (SAML 2.0)", hint: "ADFS, Okta SAML, OneLogin, PingFederate..." },
];

const PROVIDER_LABEL: Record<string, string> = {
  google: "Google Workspace",
  microsoft: "Microsoft Entra ID",
  oidc: "OpenID Connect",
  saml: "SAML 2.0",
  other: "Identity provider",
};

/**
 * Settings → Members → Single sign-on. Connect the company identity provider
 * so members can sign in with it. SSO only signs in people who are already
 * members (matched by email); invites stay the way in.
 */
const SsoSettings: React.FC<{ tenantId: string; onChanged?: () => void }> = ({ tenantId, onChanged }) => {
  const [providers, setProviders] = useState<SsoProvider[] | null>(null);
  const [setup, setSetup] = useState<SsoSetupInfo | null>(null);
  const [error, setError] = useState("");
  const [showConnect, setShowConnect] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError("");
    try {
      const [p, s] = await Promise.all([membersApi.ssoList(tenantId), membersApi.ssoSetupInfo(tenantId)]);
      setProviders(p.data);
      setSetup(s.data);
    } catch (e) {
      setError(membersErr(e, "Could not load single sign-on settings"));
      setProviders([]);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (p: SsoProvider) => {
    if (!window.confirm(`Disconnect ${p.name}? Dashboard members will sign in with their password instead.`)) return;
    setBusy(p.id);
    try {
      await membersApi.ssoRemove(tenantId, p.id);
      toast.success(`${p.name} disconnected`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(membersErr(e, "Could not disconnect"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="acct-card">
      <div className="acct-card__head">
        <div>
          <h3>Dashboard single sign-on</h3>
          <p>
            Let dashboard members sign in to <strong>this admin dashboard</strong> with your company
            identity provider. Existing members are matched by email; anyone else who signs in this
            way appears above as an access request for you to approve. This does not affect the
            ChargingAdda mobile app, which will get its own single sign-on setup.
          </p>
        </div>
        <button className="acct-btn acct-btn--primary acct-btn--small" onClick={() => setShowConnect(true)}>
          <KeyRound size={14} /> Connect provider
        </button>
      </div>

      {error && <div className="acct-notice acct-notice--error" style={{ marginBottom: 12 }}>{error}</div>}

      {providers === null ? (
        <p className="acct-loading">Loading...</p>
      ) : providers.length === 0 ? (
        <p className="acct-row__hint">No provider connected. Dashboard members sign in with a password (and 2FA if set up).</p>
      ) : (
        providers.map((p) => (
          <div className="acct-row" key={p.id}>
            <div className="acct-row__main">
              <div className="acct-row__icon"><Building2 size={18} /></div>
              <div>
                <div className="acct-row__label">
                  {p.name}
                  {p.enabled && p.state === "IDP_STATE_ACTIVE" ? (
                    <span className="acct-badge acct-badge--ok">Enabled</span>
                  ) : (
                    <span className="acct-badge acct-badge--warn">Not active</span>
                  )}
                </div>
                <div className="acct-row__hint">{PROVIDER_LABEL[p.provider] || p.provider}</div>
                {p.saml && (
                  <div style={{ marginTop: 8 }}>
                    <div className="acct-row__hint">Register at your IdP if not done yet:</div>
                    <div className="acct-row__hint">Entity ID / SP metadata</div>
                    <CopyValue value={p.saml.metadata_url} />
                    <div className="acct-row__hint" style={{ marginTop: 6 }}>ACS URL</div>
                    <CopyValue value={p.saml.acs_url} />
                  </div>
                )}
              </div>
            </div>
            <div className="acct-row__actions">
              <button className="acct-btn acct-btn--small acct-btn--danger" disabled={busy === p.id} onClick={() => remove(p)}>
                {busy === p.id ? "Removing..." : "Disconnect"}
              </button>
            </div>
          </div>
        ))
      )}

      {showConnect && setup && (
        <ConnectModal
          tenantId={tenantId}
          redirectUri={setup.redirect_uri}
          onClose={() => setShowConnect(false)}
          onDone={() => {
            setShowConnect(false);
            load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
};

const CopyValue: React.FC<{ value: string }> = ({ value }) => {
  const [c, setC] = useState(false);
  return (
    <div className="acct-inline" style={{ marginTop: 6 }}>
      <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
      <button
        type="button"
        className="acct-btn acct-btn--small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setC(true);
            setTimeout(() => setC(false), 1500);
          } catch {
            /* noop */
          }
        }}
      >
        {c ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
};

const ConnectModal: React.FC<{
  tenantId: string;
  redirectUri: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ tenantId, redirectUri, onClose, onDone }) => {
  // 1 provider -> 2 register at IdP -> 3 credentials -> (SAML) 4 SP details
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [provider, setProvider] = useState<SsoConnectReq["provider"]>("google");
  const [f, setF] = useState({ name: "", client_id: "", client_secret: "", issuer: "", azure_tenant_id: "", metadata_url: "", metadata_xml: "", signed_requests: false });
  const [samlMode, setSamlMode] = useState<"url" | "xml">("url");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<SsoProvider | null>(null);

  const providerLabel = PROVIDERS.find((p) => p.key === provider)?.label || "";
  const isSaml = provider === "saml";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await membersApi.ssoConnect(
        tenantId,
        isSaml
          ? {
              provider,
              name: f.name || undefined,
              metadata_url: samlMode === "url" ? f.metadata_url : undefined,
              metadata_xml: samlMode === "xml" ? f.metadata_xml : undefined,
              signed_requests: f.signed_requests,
            }
          : {
              provider,
              name: f.name || undefined,
              client_id: f.client_id,
              client_secret: f.client_secret,
              issuer: provider === "oidc" ? f.issuer : undefined,
              azure_tenant_id: provider === "microsoft" ? f.azure_tenant_id || undefined : undefined,
            },
      );
      toast.success(`${r.data.name} connected`);
      if (r.data.saml) {
        setCreated(r.data);
        setStep(4);
      } else {
        onDone();
      }
    } catch (err) {
      setError(membersErr(err, "Could not connect the provider"));
    } finally {
      setBusy(false);
    }
  };

  const registerSteps: Record<SsoConnectReq["provider"], string[]> = {
    google: [
      "Open Google Cloud Console → APIs & Services → Credentials.",
      "Create an OAuth client ID of type Web application.",
      "Add the redirect URI below to Authorised redirect URIs.",
      "Copy the Client ID and Client secret for the next step.",
    ],
    microsoft: [
      "Open the Microsoft Entra admin center → App registrations → New registration.",
      "Platform: Web. Add the redirect URI below.",
      "Under Certificates & secrets create a client secret.",
      "Copy the Application (client) ID and the secret value for the next step.",
    ],
    oidc: [
      "In your identity provider create an OpenID Connect web client.",
      "Register the redirect URI below.",
      "Note the issuer URL, client ID and client secret for the next step.",
    ],
    saml: [
      "In your identity provider create a new SAML 2.0 application.",
      "Locate its IdP metadata URL (or download the metadata XML).",
      "Release the email, first name and last name attributes in the assertion.",
      "You will get ChargingAdda's Entity ID and ACS URL to paste back after connecting.",
    ],
  };

  const totalSteps = isSaml ? 4 : 3;
  const Steps = () => (
    <div className="acct-steps" aria-label={`Step ${step} of ${totalSteps}`}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <span key={i} className={i < step ? "done" : ""} />
      ))}
    </div>
  );

  return (
    <div className="acct-modal-overlay" onClick={step === 4 ? onDone : onClose}>
      <form className="acct-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="acct-modal__head">
          <h3>Connect dashboard single sign-on</h3>
          <button type="button" onClick={step === 4 ? onDone : onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <Steps />

        {step === 1 && (
          <>
            <div className="acct-step-label">Step 1 · Provider</div>
            <p>Which identity provider should dashboard members sign in with? This applies to the admin dashboard only.</p>
            <div className="role-picker">
              {PROVIDERS.map((p) => (
                <label key={p.key}>
                  <input type="radio" name="provider" checked={provider === p.key} onChange={() => setProvider(p.key)} />
                  <span>
                    {p.label}
                    <small>{p.hint}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="acct-actions">
              <button type="button" className="acct-btn" onClick={onClose}>Cancel</button>
              <button type="button" className="acct-btn acct-btn--primary" onClick={() => setStep(2)}>Next</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="acct-step-label">Step 2 · Set up at {providerLabel}</div>
            <p>Have your IT admin do this on the provider's side. It takes a few minutes.</p>
            <ol style={{ margin: "0 0 12px", paddingLeft: 20, fontSize: 14, color: "#111827", lineHeight: 1.6 }}>
              {registerSteps[provider].map((t, i) => <li key={i}>{t}</li>)}
            </ol>
            {!isSaml && (
              <div className="acct-notice acct-notice--info" style={{ display: "block" }}>
                <div style={{ fontWeight: 600 }}>Redirect URI to register</div>
                <CopyValue value={redirectUri} />
              </div>
            )}
            <div className="acct-actions">
              <button type="button" className="acct-btn" onClick={() => setStep(1)}>Back</button>
              <button type="button" className="acct-btn acct-btn--primary" onClick={() => setStep(3)}>I have this, next</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="acct-step-label">Step 3 · Connection details</div>
            <div className="acct-form">
              <label className="full">
                Display name <small>(shown on the sign-in button)</small>
                <input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={providerLabel} maxLength={100} />
              </label>
              {isSaml ? (
                <>
                  <div className="full" style={{ display: "flex", gap: 14, fontSize: 13 }}>
                    <label style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
                      <input type="radio" checked={samlMode === "url"} onChange={() => setSamlMode("url")} /> Metadata URL
                    </label>
                    <label style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
                      <input type="radio" checked={samlMode === "xml"} onChange={() => setSamlMode("xml")} /> Paste metadata XML
                    </label>
                  </div>
                  {samlMode === "url" ? (
                    <label className="full">
                      IdP metadata URL
                      <input type="url" value={f.metadata_url} onChange={(e) => setF({ ...f, metadata_url: e.target.value })} placeholder="https://login.example.com/saml/metadata" required />
                    </label>
                  ) : (
                    <label className="full">
                      IdP metadata XML
                      <textarea
                        value={f.metadata_xml}
                        onChange={(e) => setF({ ...f, metadata_xml: e.target.value })}
                        rows={5}
                        required
                        style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: 10, fontSize: 12, fontFamily: "monospace" }}
                        placeholder="<EntityDescriptor ...>"
                      />
                    </label>
                  )}
                  <label className="full" style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
                    <input type="checkbox" checked={f.signed_requests} onChange={(e) => setF({ ...f, signed_requests: e.target.checked })} />
                    Sign authentication requests (only if your IdP requires it)
                  </label>
                </>
              ) : (
                <>
                  {provider === "oidc" && (
                    <label className="full">
                      Issuer URL
                      <input type="url" value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} placeholder="https://login.example.com" required />
                    </label>
                  )}
                  {provider === "microsoft" && (
                    <label className="full">
                      Directory (tenant) ID <small>(optional — empty allows any work account)</small>
                      <input value={f.azure_tenant_id} onChange={(e) => setF({ ...f, azure_tenant_id: e.target.value })} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                    </label>
                  )}
                  <label className="full">
                    Client ID
                    <input value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} required maxLength={200} />
                  </label>
                  <label className="full">
                    Client secret
                    <input type="password" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} required maxLength={500} autoComplete="off" />
                  </label>
                </>
              )}
            </div>
            {error && <p className="acct-form-error">{error}</p>}
            <div className="acct-actions">
              <button type="button" className="acct-btn" onClick={() => setStep(2)} disabled={busy}>Back</button>
              <button type="submit" className="acct-btn acct-btn--primary" disabled={busy}>
                {busy ? "Connecting..." : "Connect"}
              </button>
            </div>
          </>
        )}

        {step === 4 && created?.saml && (
          <>
            <div className="acct-step-label">Step 4 · Finish at your IdP</div>
            <p><strong>{created.name}</strong> is connected. Register ChargingAdda as a service provider with these values; they stay visible in the Single sign-on list.</p>
            <div className="acct-row__hint">Entity ID / SP metadata URL</div>
            <CopyValue value={created.saml.metadata_url} />
            <div className="acct-row__hint" style={{ marginTop: 10 }}>ACS (reply) URL</div>
            <CopyValue value={created.saml.acs_url} />
            <div className="acct-row__hint" style={{ marginTop: 10 }}>Name ID format: email address.</div>
            <div className="acct-actions">
              <button type="button" className="acct-btn acct-btn--primary" onClick={onDone}>Done</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default SsoSettings;
